import { useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Login step-up. The server holds the pending challenge (opaque mfa_challenge cookie);
// we only submit a TOTP or backup code. On success the real session cookie is set.
export default function MfaChallenge({ onSuccess, onCancel }) {
  const [useBackup, setUseBackup] = useState(false);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = useBackup ? { backup_code: backup.trim() } : { code: code.trim() };
      payload.remember_device = remember;
      const { data } = await api.post("/auth/mfa/verify", payload);
      onSuccess(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error("Too many attempts. Please sign in again.");
        onCancel?.();
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="mfa-challenge">
      <div className="flex items-center gap-2 text-[#2563EB]">
        <KeyRound size={20} />
        <h2 className="font-heading font-bold text-lg text-[#0A0F1C]">Two-factor verification</h2>
      </div>
      <p className="text-sm text-slate-500">
        {useBackup ? "Enter one of your saved backup codes." : "Enter the 6-digit code from your authenticator app."}
      </p>
      <form onSubmit={submit} className="space-y-4">
        {useBackup ? (
          <input
            value={backup} onChange={(e) => setBackup(e.target.value)} required
            data-testid="mfa-challenge-backup-input" placeholder="XXXX-XXXX" autoFocus
            className="w-full h-11 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] outline-none font-mono tracking-widest"
          />
        ) : (
          <input
            inputMode="numeric" pattern="[0-9]*" maxLength={6} required autoFocus
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="mfa-challenge-code-input" placeholder="123456"
            className="w-full h-11 tracking-[0.5em] text-center text-lg px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] outline-none"
          />
        )}
        <label className="flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer" data-testid="mfa-remember-device-label">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
            data-testid="mfa-remember-device-checkbox" className="h-4 w-4 rounded border-slate-300 text-[#2563EB] focus:ring-[#2563EB]" />
          Trust this device for 30 days
        </label>
        <Button
          type="submit" disabled={busy || (useBackup ? backup.length < 4 : code.length !== 6)}
          data-testid="mfa-challenge-submit-btn"
          className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8]"
        >
          {busy ? <Loader2 className="animate-spin" size={18} /> : "Verify"}
        </Button>
      </form>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={() => setUseBackup((v) => !v)} className="text-[#2563EB]" data-testid="mfa-challenge-toggle-backup">
          {useBackup ? "Use authenticator code" : "Use a backup code"}
        </button>
        <button type="button" onClick={onCancel} className="text-slate-400" data-testid="mfa-challenge-cancel">Back to sign in</button>
      </div>
    </div>
  );
}
